import React, { useEffect, useRef, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';

// Some browsers (Safari) don't load a first frame until play is pressed, so the
// player is shown once its size is known even if no frame has arrived yet.
const METADATA_FALLBACK_MS = 800;
// Safari on iOS may not preload at all, so no media event fires until play is
// pressed. If the browser isn't downloading after this long, show the player so
// its play button can be reached.
const IDLE_FALLBACK_MS = 2000;
// Hard cap: never leave the frame as a placeholder longer than this.
const MAX_PLACEHOLDER_MS = 15000;

interface LessonVideoPlayerProps {
  src: string;
  maxWidth?: number | string;
  sx?: SxProps<Theme>;
}

/**
 * A lesson video that holds its full-width frame from the first paint.
 *
 * A bare <video> starts as the browser's small default player (300x150) with
 * its own spinner, then jumps to full size once the file's dimensions load.
 * Here the frame is sized up front (16:9, corrected to the file's own ratio
 * while still hidden), a dark placeholder shows while the file loads, and the
 * video fades in once it's ready.
 */
const LessonVideoPlayer: React.FC<LessonVideoPlayerProps> = ({ src, maxWidth, sx }) => {
  const [ready, setReady] = useState(false);
  const [ratio, setRatio] = useState('16 / 9');
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    setReady(false);
    const started = Date.now();
    const check = window.setInterval(() => {
      const video = videoRef.current;
      const idle = !video || video.networkState !== HTMLMediaElement.NETWORK_LOADING;
      const waited = Date.now() - started;
      if ((idle && waited >= IDLE_FALLBACK_MS) || waited >= MAX_PLACEHOLDER_MS) {
        setReady(true);
        window.clearInterval(check);
      }
    }, 500);
    return () => window.clearInterval(check);
  }, [src]);

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const { videoWidth, videoHeight } = e.currentTarget;
    if (videoWidth && videoHeight) setRatio(`${videoWidth} / ${videoHeight}`);
    window.setTimeout(() => setReady(true), METADATA_FALLBACK_MS);
  };

  return (
    <Box
      sx={[
        {
          position: 'relative',
          width: '100%',
          maxWidth,
          aspectRatio: ratio,
          borderRadius: '8px',
          overflow: 'hidden',
          bgcolor: '#141414'
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      {!ready && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, #141414 0%, #1E1E1E 50%, #141414 100%)',
            backgroundSize: '200% 100%',
            animation: 'lessonVideoShimmer 1.4s ease-in-out infinite',
            '@keyframes lessonVideoShimmer': {
              from: { backgroundPosition: '100% 0' },
              to: { backgroundPosition: '-100% 0' }
            }
          }}
        />
      )}
      <video
        ref={videoRef}
        src={src}
        controls
        playsInline
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={() => setReady(true)}
        onError={() => setReady(true)}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          background: '#000',
          opacity: ready ? 1 : 0,
          transition: 'opacity 0.25s ease'
        }}
      />
    </Box>
  );
};

export default LessonVideoPlayer;
